// What an exam's staff and its sitting share between Share's exam page
// (/ea/{id}, pages/ExamAssignment) and the exam edition's console (RFC-062,
// byj-1wv.118.3): a grade's reasons, disputes and drawer, the hand-ins
// table, who is sitting, the instructor's view of an assignment as tabs, its
// bar, and the exam's own machine.
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, type ExamAssignmentPage, type ExamAttempt, type ExamDispute, type ExamSitting, type ExamGrade, type ExamReview, type ExamSubmission, type GradeDetail, type MachinesView } from "../api";
import { CommandLine } from "./CommandLine";
import { Drawer } from "./Drawer";
import { AuditPanel, GradeReviews, ReviewPanel } from "./ExamReview";
import { FilterChips, inForce, useFilter, type Chip } from "./FilterChips";
import { FormDialog } from "./FormDialog";
import { Link } from "./Nav";
import { EmptyRows, initialOf } from "./Rows";
import { StateBar } from "./StateBar";
import { panelId, tabId, Tabs } from "./Tabs";
import { useKeptChoice } from "../kept";
import { navigate } from "../router";
import { competencies } from "../competencies";
import { download, gradeState, openDispute, scoreOf, signalName, submissionsCsv, windowState } from "../exams";
import { t, whenMinute, word, phrases } from "../i18n";
import { openClassExams } from "../classManage";
import { pollMs, pollStartingMs } from "../machines";
import { useQuestionName, useStaff, type StaffTab } from "../staff";
import { agentLabel } from "../workbench";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

const fmt = (part: string, v: number | null | undefined) =>
  v == null ? "–" : part === "cost-usd" ? `$${v.toFixed(4)}` : part === "seconds" ? `${Math.round(v)} s` : String(v);

/** A grade's reasons: its points, checks or trials, measures, the agent, Prism. */
export function GradeReasons({ detail, recording }: { detail?: GradeDetail; recording?: string }) {
  const staff = useStaff();
  if (!detail) return null;
  const r = detail.reasons ?? {};
  const p = detail.parts;
  const eff = p?.efficiency ? Object.values(p.efficiency).reduce<number>((n, v) => n + (v ?? 0), 0) : null;
  return (
    <div className="stack grade-reasons" data-testid="grade-reasons">
      {detail.error && <p className="error">{t("exams:gradeError", { e: detail.error })}</p>}
      {p && (
        <div className="doc-table exam-table grade-parts" tabIndex={0}>
          <table data-testid="grade-parts">
            <thead>
              <tr>
                <th>{t("exams:colPart")}</th>
                <th>{t("exams:colScore")}</th>
              </tr>
            </thead>
            <tbody>
              {(p.outcome != null
                ? ["outcome", ...competencies].map((k) => [word("exams:rubricPart", k), String(p[k as keyof typeof p] ?? 0)] as const)
                : ([
                    [t("exams:rubricPart.accuracy"), String(p.accuracy ?? 0)],
                    [t("exams:rubricPart.agent"), String(p.agent ?? 0)],
                    [t("exams:rubricPart.efficiency"), eff?.toFixed(1) ?? "0"],
                    [t("exams:rubricPart.process"), String(p.process ?? 0)],
                  ] as const)
              ).map(([part, points]) => (
                <tr key={part}>
                  <td>{part}</td>
                  <td>{points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {r.fluency && (
        <div className="stack" data-testid="grade-fluency">
          {competencies.map((k) => {
            const c = r.fluency?.[k];
            if (!c) return null;
            return (
              <div key={k} className="small stack">
                <strong>
                  {word("exams:rubricPart", k)} {c.points} / {c.of}
                  {c.value == null && <span className="muted"> — {t("exams:fluencyUnobserved")}</span>}
                </strong>
                <ul className="flush">
                  {c.signals.map((s) => (
                    <li key={s.signal}>
                      {s.value >= 1 ? "✓" : s.value > 0 ? "~" : "✗"} <span title={s.signal}>{signalName(s.signal)}</span>
                      {s.of != null && ` ${s.met}/${s.of}`}
                      {s.detail && <span className="muted"> — {s.detail}</span>}
                      {(s.quotes ?? []).slice(0, 1).map((q) => (
                        <span key={q.quote} className="muted">
                          {" "}
                          — “{q.quote}”{q.at != null && ` (${new Date(q.at).toLocaleTimeString()})`}
                        </span>
                      ))}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
      {r.verdicts && r.verdicts.length > 0 && (
        <>
          <h4 className="flush">{t("exams:gradeChecks")}</h4>
          <p className="hint flush">{t("exams:gradeChecksHint")}</p>
          <ul className="small flush" data-testid="grade-checks">
            {r.verdicts.map((v, i) => (
              <li key={i}>
                {v["pass?"] ? "✓" : "✗"} {v.name}
                {v.detail && <span className="muted"> — {v.detail}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
      {r.trials && r.trials.length > 0 && (
        <>
          <h4 className="flush">{t("exams:gradeTrials")}</h4>
          <ul className="small flush">
            {r.trials.map((tr) => (
              <li key={tr.trial}>
                {tr["resolved?"] ? "✓" : "✗"} <code>{tr.trial}</code> {tr.score.toFixed(1)}
              </li>
            ))}
          </ul>
        </>
      )}
      {r.measures && r.measures.length > 0 && (
        <>
          <h4 className="flush">{t("exams:gradeMeasures")}</h4>
          <p className="hint flush">{t("exams:gradeMeasuresHint")}</p>
          <ul className="small flush" data-testid="grade-measures">
            {r.measures.map((m) => (
              <li key={m.part}>
                {word("exams:effLabel", m.part) ?? m.part} {fmt(m.part, m.value)}
                {m.budget != null && ` / ${fmt(m.part, m.budget)}`} — {m.of != null ? t("exams:measurePoints", { points: m.points ?? 0, of: m.of }) : t("exams:measureReported")}
              </li>
            ))}
          </ul>
        </>
      )}
      {r.agent?.wanted && (
        <p className="small flush">
          {r.agent["ok?"] ? t("exams:gradeAgentUsed", { a: r.agent.wanted }) : t("exams:gradeAgentNotUsed", { a: r.agent.wanted })}
          {r.agent.used && r.agent.used.length > 0 && <span className="muted"> ({phrases.gradeAgentsUsed(r.agent.used)})</span>}
        </p>
      )}
      {r.process && (
        <>
          <h4 className="flush">{t("exams:gradeProcess")}</h4>
          <p className="hint flush">{t("exams:gradeProcessHint")}</p>
          <p className="small flush">{r.process.score != null ? r.process.score.toFixed(2) : phrases.gradeProcessNone(r.process.why ?? "")}</p>
          {(r.process.tasks ?? []).map((task) => {
            const unmet = Object.entries(task.items ?? {}).filter(([, v]) => v.verdict === "unmet");
            const met = Object.entries(task.items ?? {}).filter(([, v]) => v.verdict === "met" && v.quote);
            return (
              <div key={task["task-id"]} className="small stack">
                {task.error && <span className="error">{task.error}</span>}
                {unmet.length > 0 && (
                  <span>
                    {t("exams:gradeUnmet")}: {unmet.map(([k]) => signalName(k)).join(", ")}
                  </span>
                )}
                {met.map(([k, v]) => (
                  <span key={k} className="muted">
                    ✓ {signalName(k)} — “{v.quote}”
                  </span>
                ))}
              </div>
            );
          })}
        </>
      )}
      {detail.flags && detail.flags.length > 0 && (
        <>
          <h4 className="flush">{t("exams:gradeFlags")}</h4>
          <ul className="small flush" data-testid="grade-flags">
            {detail.flags.map((f) => (
              <li key={f} className="error">
                {f}
              </li>
            ))}
          </ul>
        </>
      )}
      {recording && staff.recordings && (
        <a href={`/r/${encodeURIComponent(recording)}`} className="small">
          {t("exams:gradeRecording")}
        </a>
      )}
    </div>
  );
}

/** A hand-in's disputes and their answers; an open one answerable by its instructor. */
export function Disputes({ disputes, answer }: { disputes: ExamDispute[]; answer?: (id: string, text: string) => Promise<void> }) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (disputes.length === 0) return null;
  return (
    <div className="stack" data-testid="disputes">
      <h3 className="flush">{t("exams:disputeTitle")}</h3>
      {disputes.map((d) => (
        <div key={d.id} className="small stack dispute">
          <span>“{d.text}”</span>
          {d.answered_at ? (
            <span className="muted">
              {t("exams:disputeAnswer")}: {d.answer}
            </span>
          ) : answer ? (
            <form
              className="row wrap"
              onSubmit={(e) => {
                e.preventDefault();
                if (!text.trim()) return;
                setBusy(true);
                setError(null);
                answer(d.id, text.trim()).then(
                  () => setText(""),
                  (err: unknown) => setError(errText(err)),
                ).finally(() => setBusy(false));
              }}
            >
              <input className="grow" aria-label={t("exams:disputeAnswer")} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} />
              <button type="submit" disabled={busy || !text.trim()}>
                {t("exams:disputeAnswerSend")}
              </button>
            </form>
          ) : (
            <span className="muted">{t("exams:disputeWaiting")}</span>
          )}
        </div>
      ))}
      {error && <p className="error">{t("common:error", { m: error })}</p>}
    </div>
  );
}

/**
 * A student's dispute of their released grade: a line saying why, asked in
 * a short form over the page (components/FormDialog), which gives the focus
 * back to the grade's Dispute. Drawn by the student's view, not inside the
 * question: a question folded hides what is inside it, a dialog too.
 */
export function DisputeDialog({ send, onClose }: { send: (text: string) => Promise<void>; onClose: () => void }) {
  const [text, setText] = useState("");
  return (
    <FormDialog
      id="dispute-dialog"
      title={t("exams:disputeGrade")}
      submitLabel={t("exams:disputeSend")}
      canSubmit={text.trim() !== ""}
      onSubmit={() => send(text.trim()).then(onClose)}
      onClose={onClose}
    >
      <label className="field">
        {t("exams:disputeWhy")}
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} />
      </label>
    </FormDialog>
  );
}

/** The score as it counts, and what it was before an override. */
export function Score({ g }: { g: ExamGrade }) {
  return (
    <span>
      <strong>{scoreOf(g).toFixed(1)}</strong>
      {g.override != null && <span className="muted small"> ({t("exams:overridden", { from: g.score })})</span>}
    </span>
  );
}

/**
 * The instructor's drawer on one hand-in: its grade, its reviews, an
 * override, release — a reviewer's, its grade and its reviews (RFC-055).
 * With review on, a grade is released once the assignment's grades are
 * finished; finished, they are locked.
 */
export function GradeDrawer({ sub, questions = [], role, review, onClose, onSaved, onAnswered, onReviewed, patch, answer }: {
  sub: ExamSubmission;
  /** the paper's questions by id, in order, as Submissions has them */
  questions?: readonly string[];
  role: ExamAssignmentPage["role"];
  review?: ExamAssignmentPage["assignment"]["review"];
  onClose: () => void;
  onSaved: (g: ExamGrade) => void;
  onAnswered: (d: ExamDispute) => void;
  onReviewed: (r: ExamReview) => void;
  patch: typeof api.patchGrade;
  answer: typeof api.answerDispute;
}) {
  const staff = useStaff();
  const questionName = useQuestionName(questions);
  const g = sub.grade;
  const finished = !!review?.finalized_at;
  const holdRelease = !!review?.review && !finished;
  const [override, setOverride] = useState(g?.override != null ? String(g.override) : "");
  const [note, setNote] = useState(g?.override_note ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async (released?: boolean) => {
    if (!g) return;
    setBusy(true);
    setError(null);
    try {
      const n = override.trim() === "" ? undefined : Number(override);
      if (n !== undefined && (Number.isNaN(n) || n < 0 || n > 100)) throw new Error("0–100");
      onSaved(await patch(g.id, { ...(n === undefined ? { clear_override: true } : { override: n }), note, ...(released === undefined ? {} : { released }) }));
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Drawer id="grade-drawer" className="drawer-wide" title={t("exams:gradeTitle", { who: sub.student?.name || sub.student?.email || "?", q: questionName(sub.question) })} open onClose={onClose}>
      <div className="stack">
        {/* where the hand-in stands, at a glance: its score and state, then what it was */}
        <div className="card pad stack tight" data-testid="grade-summary">
          <p className="flush grade-summary-score">
            {g ? <Score g={g} /> : <span className="muted">–</span>}{" "}
            <GradeChip sub={sub} />{" "}
            {sub.late && <span className="chip chip-warn">{t("classes:late")}</span>}
          </p>
          <p className="small muted flush">
            {sub.agent ? agentLabel(sub.agent) : "–"} · #{sub.n} · {whenMinute(sub.submitted_at)}
          </p>
        </div>
        {!g ? (
          // not graded yet: who grades it, what this drawer will offer once it is, and how to see that
          <section className="grade-step stack tight" data-testid="grade-pending">
            <h3 className="flush">{t("exams:gradePendingTitle")}</h3>
            <p className="flush">{staff.gradedHere ? t("exams:gradeNotYet") : t("console:gradeNotYet")}</p>
            <p className="muted small flush">{t("exams:gradePendingNext")}</p>
            <ul className="small flush">
              <li>{t("exams:gradePendingEvidence")}</li>
              <li>{t("exams:gradePendingReview")}</li>
              {role === "instructor" && <li>{t("exams:gradePendingDecide")}</li>}
            </ul>
            <p className="hint flush">{t("exams:gradePendingRefresh")}</p>
          </section>
        ) : (
          <>
            {/* three numbered parts, each a card that says what it is for: the grader's evidence, the reviewers' say, and the final score and its release */}
            <section className="grade-step stack tight" data-testid="grade-result">
              <h3 className="flush">{t("exams:gradeResultTitle")}</h3>
              <p className="muted small flush">{t("exams:gradeResultLead")}</p>
              <GradeReasons detail={g.detail} recording={sub.recording_id} />
            </section>
            <section className="grade-step">
              <GradeReviews
                gradeId={g.id}
                score={g.score}
                reviews={sub.reviews ?? []}
                locked={finished}
                onReviewed={onReviewed}
                signals={Object.values(g.detail?.reasons?.fluency ?? {}).flatMap((c) => c.signals.filter((s) => s.by === "prism").map((s) => s.signal))}
              />
            </section>
            <Disputes disputes={sub.disputes ?? []} answer={role === "instructor" ? async (id, text) => onAnswered(await answer(id, text)) : undefined} />
            {openDispute(sub) && staff.gradedHere && (
              <p className="muted small flush">
                {t("exams:disputeRegradeHint")}
                <code>{sub.id}</code>
              </p>
            )}
            {role === "instructor" && (
            <form
              className="grade-step stack"
              data-testid="grade-decide"
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
            >
              <h3 className="flush">{t("exams:gradeDecideTitle")}</h3>
              <p className="muted small flush">{t("exams:gradeDecideLead")}</p>
              <label>
                {t("exams:gradeOverride")}
                <input inputMode="decimal" value={override} onChange={(e) => setOverride(e.target.value)} placeholder={g.score.toFixed(1)} disabled={finished} />
              </label>
              <label>
                {t("exams:gradeOverrideNote")}
                <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} disabled={finished} />
              </label>
              {holdRelease && <p className="muted small flush">{t("exams:releaseAfterFinish")}</p>}
              {error && <p className="error">{t("common:error", { m: error })}</p>}
              <div className="row wrap">
                <button type="submit" disabled={busy || finished}>
                  {t("exams:gradeSave")}
                </button>
                {g.released ? (
                  <button type="button" disabled={busy} onClick={() => void save(false)}>
                    {t("exams:gradeHold")}
                  </button>
                ) : (
                  <button type="button" className="primary" disabled={busy || holdRelease} onClick={() => void save(true)}>
                    {staff.release()}
                  </button>
                )}
              </div>
            </form>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}

/** A hand-in's grade state as a chip: waiting marked, released live — the table's and the drawer's alike. */
function GradeChip({ sub }: { sub: ExamSubmission }) {
  const state = gradeState(sub);
  return <span className={`chip${state === "released" ? " chip-live" : state === "waiting" ? " chip-warn" : ""}`}>{word("exams:gradeState", state)}</span>;
}

const liveOf = (a: ExamAttempt) => !!a.share_id && !!a.share_live;

/** A student in Sitting now: their attempts listed, the live one watched, and their sitting if it closed. */
type SittingRow = { key: string; attempts: ExamAttempt[]; live?: ExamAttempt; closed?: ExamSitting };

/**
 * Who is in Sitting now, a row a student: Begin starts every question's
 * attempt at once (RFC-058 D1), so a student is one row, counted once, and
 * what is live is counted by share (byj-1wv.118.14). An open sitting lists
 * the attempts not handed in. A sitting closed, its time up or finished, is
 * sat no more however much was handed in, and is listed only while its
 * machine runs, to be watched until it stops, as the console still shows it
 * live. `sitting` counts the open ones, as the console's head does.
 */
const sittingOf = (page: ExamAssignmentPage) => {
  const closed = new Map((page.sittings ?? []).flatMap((s) => (s.closed && s.student ? [[s.student.id, s] as const] : [])));
  const rows = new Map<string, SittingRow>();
  for (const a of page.attempts) {
    const c = a.student ? closed.get(a.student.id) : undefined;
    if (c ? !liveOf(a) : a.submissions.some((s) => s.uploaded)) continue;
    const key = a.student?.id ?? a.id;
    const row = rows.get(key) ?? { key, attempts: [], closed: c };
    row.attempts.push(a);
    if (!row.live && liveOf(a)) row.live = a;
    rows.set(key, row);
  }
  const listed = [...rows.values()];
  return {
    listed,
    sitting: listed.filter((r) => !r.closed).length,
    machines: new Set(listed.flatMap((r) => r.attempts.filter(liveOf).map((a) => a.share_id))).size,
  };
};

/** Who is sitting what, and the share to watch it in (byj-1wv.56.9). */
/** Where a sitting is (RFC-058): its deadline, its hand-ins, whether its time is up. */
export function SittingFacts({ s }: { s: ExamSitting }) {
  return (
    <>
      {s.deadline ? t("exams:sittingDeadline", { w: whenMinute(s.deadline) }) : t("exams:sittingNoDeadline")}
      {" · "}
      {t("exams:sittingHanded", { n: s.handed_in, total: s.questions })}{" "}
      <span className={`chip${s.closed ? "" : s.over ? " chip-warn" : " chip-live"}`}>
        {s.finished_at ? t("exams:sittingFinished") : s.closed ? t("exams:sittingClosed") : s.over ? t("exams:sittingOver") : t("exams:sittingRunning")}
      </span>
    </>
  );
}

export function Sitting({ page, listed }: { page: ExamAssignmentPage; listed: SittingRow[] }) {
  const staff = useStaff();
  return (
    <>
      {(page.sittings ?? []).length > 0 && (
        <section aria-labelledby="exam-sittings" className="stack">
          <h3 id="exam-sittings" className="rows-head flush">
            {t("exams:sittingsHead")} · {page.sittings!.length}
          </h3>
          <ul className="rows" data-testid="exam-sittings">
            {page.sittings!.map((x) => (
              <li key={x.student?.id ?? x.started_at} className="share-row exam-row no-menu">
                <span className="row-lead" />
                <div className="row-main">
                  <span className="row-title">{x.student?.name || x.student?.email || "?"}</span>
                  <div className="row-sub">
                    <SittingFacts s={x} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      {listed.length === 0 ? (
        <EmptyRows text={t("exams:nobodySitting")} />
      ) : (
        <ul className="rows" data-testid="exam-sitting">
          {listed.map((r) => {
            const a = r.attempts[0]!;
            const who = a.student?.name || a.student?.email || "?";
            const watch = r.live ? staff.watch(r.live) : undefined;
            // a closed sitting, its machine not yet stopped: watched still, said to be closed, its questions no more
            const closed = r.closed;
            const shared = r.attempts.some((x) => x.share_id);
            return (
              <li key={r.key} className={`share-row exam-row no-menu ${r.live ? "is-live" : "is-ended"}`}>
                <span className="row-lead" />
                <span className="kind-box" aria-hidden="true">
                  {initialOf(who)}
                </span>
                <div className="row-main">
                  <span className="row-title">{who}</span>
                  <div className="row-sub">
                    {!closed &&
                      r.attempts.map((x) => (
                        <span key={x.id}>
                          <code>{x.question}</code> ·{" "}
                        </span>
                      ))}
                    {a.agent} · {t("shares:startedAt", { when: whenMinute(a.started_at) })}
                  </div>
                </div>
                <div className="row-meta">
                  {closed && (
                    <span className="chip" data-testid="sitting-closed">
                      {closed.finished_at ? t("exams:sittingFinished") : t("exams:sittingClosed")}
                    </span>
                  )}
                  {r.live ? (
                    watch && (
                      <Link to={watch} className="button small">
                        {t("exams:watchLive")}
                      </Link>
                    )
                  ) : (
                    <span className={`chip ${shared ? "" : "chip-warn"}`}>{shared ? t("exams:sharedEnded") : t("exams:notShared")}</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

type SubFilter = "all" | "waiting" | "graded" | "released" | "disputed";
const matchesFilter = (s: ExamSubmission, f: string) => f === "all" || (f === "disputed" ? !!openDispute(s) : gradeState(s) === f);


/**
 * The instructor's view (byj-1wv.51.12): the bar says how the assignment
 * stands — its window, its class, who is sitting, the hand-ins, how many
 * wait to be graded and are disputed — then Submissions, the table narrowed
 * by a hand-in's state, and Sitting now, as tabs.
 */
export function InstructorView({ page, now, load, patch, answer, onChanged, only }: {
  page: ExamAssignmentPage;
  now: number;
  load: typeof api.examSubmissions;
  patch: typeof api.patchGrade;
  answer: typeof api.answerDispute;
  onChanged?: () => void;
  /** one panel alone, with no bar or tabs of its own: where the page's own tabs choose it (the console's session, byj-1wv.118.15) */
  only?: StaffTab;
}) {
  const staff = useStaff();
  const a = page.assignment;
  const [subs, setSubs] = useState<ExamSubmission[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useKeptChoice<StaffTab>("exam-assignment.tab", "submissions", staff.tabs);
  const [filter, setFilter] = useFilter("exam-submissions");
  const reload = useCallback(() => {
    load(a.id).then(setSubs, (err: unknown) => setError(errText(err)));
  }, [a.id, load]);
  useEffect(reload, [reload]);
  // the bar counts who sits now, as the console does; the tab whom it lists, and the machines live among them
  const { sitting, listed, machines: live } = useMemo(() => sittingOf(page), [page]);
  const questionIds = useMemo(() => page.meta.questions.map((q) => q.id), [page.meta]);
  const count = (f: SubFilter) => (subs ?? []).filter((s) => matchesFilter(s, f)).length;
  const chips: Chip[] = [
    { value: "all", label: t("classes:filterAll"), count: subs?.length ?? 0 },
    { value: "waiting", label: t("exams:gradeState.waiting")!, count: count("waiting") },
    { value: "graded", label: t("exams:gradeState.graded")!, count: count("graded") },
    { value: "released", label: t("exams:gradeState.released")!, count: count("released") },
    { value: "disputed", label: t("exams:disputeOpen"), count: count("disputed") },
  ];
  const picked = inForce(filter, chips);
  const submissions = error ? (
    <p className="error">{t("common:error", { m: error })}</p>
  ) : !subs ? (
    <p className="muted">{t("common:loading")}</p>
  ) : (
    <Submissions title={a.exam.title} role={page.role} review={a.review} questions={questionIds} subs={subs} setSubs={setSubs} filter={picked} chips={chips} onFilter={setFilter} patch={patch} answer={answer} onChanged={onChanged} />
  );
  const review = (
    <ReviewPanel
      id={a.id}
      questions={questionIds}
      onChanged={() => {
        reload();
        onChanged?.();
      }}
    />
  );
  const questionName = useQuestionName(questionIds);
  const whose = (submission?: string) => {
    const x = subs?.find((s) => s.id === submission);
    return x ? `${x.student?.name || x.student?.email || "?"} · ${questionName(x.question)}` : undefined;
  };
  const audit = <AuditPanel id={a.id} whose={whose} />;
  if (only)
    return (
      <div className="rows-panel">
        {only === "submissions" ? submissions : only === "review" ? review : only === "sitting" ? <Sitting page={page} listed={listed} /> : audit}
      </div>
    );
  return (
    <>
      <AssignmentBar page={page} now={now}>
        <span className="share-bar-facts">
          {t("exams:sittingCount", { n: sitting })}
          {subs && (
            <>
              {" · "}
              {t("exams:handInsCount", { count: subs.length })}
            </>
          )}
        </span>
        {subs && count("waiting") > 0 && <span className="chip chip-warn">{t("exams:toGrade", { n: count("waiting") })}</span>}
        {subs && count("disputed") > 0 && <span className="chip chip-warn">{t("exams:disputedCount", { n: count("disputed") })}</span>}
      </AssignmentBar>
      <Tabs
        prefix="assignment"
        label={t("exams:assignmentTabs")}
        value={tab}
        onChange={setTab}
        tabs={staff.tabs.map((id) => ({
          id,
          label: (
            <>
              {word("exams:assignmentTab", id)}
              {(id === "sitting" || id === "submissions") && <span className="tab-count">{id === "sitting" ? listed.length : (subs?.length ?? "…")}</span>}
              {id === "sitting" && live > 0 && <span className="tab-live">{t("classes:liveCount", { n: live })}</span>}
            </>
          ),
        }))}
      />
      <div role="tabpanel" id={panelId("assignment", "submissions")} aria-labelledby={tabId("assignment", "submissions")} hidden={tab !== "submissions"} className="rows-panel">
        {submissions}
      </div>
      <div role="tabpanel" id={panelId("assignment", "review")} aria-labelledby={tabId("assignment", "review")} hidden={tab !== "review"} className="rows-panel">
        {tab === "review" && review}
      </div>
      <div role="tabpanel" id={panelId("assignment", "sitting")} aria-labelledby={tabId("assignment", "sitting")} hidden={tab !== "sitting"} className="rows-panel">
        <Sitting page={page} listed={listed} />
      </div>
      <div role="tabpanel" id={panelId("assignment", "audit")} aria-labelledby={tabId("assignment", "audit")} hidden={tab !== "audit"} className="rows-panel">
        {tab === "audit" && audit}
      </div>
    </>
  );
}

/** The instructor's table: every hand-in, narrowed by its state, with its grade's state. */
export function Submissions({ title, role, review, questions = [], person = true, subs, setSubs, filter, chips, onFilter, patch, answer, onChanged }: {
  title: string;
  /** whose each is, a column; not on a page that is one person's (the console's candidate, byj-1wv.118.15) */
  person?: boolean;
  role: ExamAssignmentPage["role"];
  review?: ExamAssignmentPage["assignment"]["review"];
  /** the paper's questions by id, in order: what a console numbers them by */
  questions?: readonly string[];
  subs: ExamSubmission[];
  setSubs: (f: (xs: ExamSubmission[] | null) => ExamSubmission[] | null) => void;
  filter: string;
  chips: Chip[];
  onFilter: (v: string) => void;
  patch: typeof api.patchGrade;
  answer: typeof api.answerDispute;
  /** a grade, a review or an answer changed in the drawer: what the page says of it is read again */
  onChanged?: () => void;
}) {
  const staff = useStaff();
  const questionName = useQuestionName(questions);
  const [open, setOpen] = useState<string | null>(null);
  const shown = subs.find((s) => s.id === open);
  const rows = subs.filter((s) => matchesFilter(s, filter));
  // hand-ins with no grade are graded on the instructor's machine, where the hidden checks are (byj-1wv.51.12)
  const waiting = subs.filter((s) => gradeState(s) === "waiting").length;
  return (
    <>
      {waiting > 0 && staff.gradedHere && (
        <div className="stack grade-hint" data-testid="grade-hint">
          <span className="small muted">{t("exams:gradeWaiting", { count: waiting })}</span>
          <CommandLine command="byj exam grade KIT --relay" />
        </div>
      )}
      {subs.length > 0 && chips.length > 0 && (
        <div className="panel-tools row wrap">
          <FilterChips label={t("classes:filterByState")} chips={chips} value={filter} onChange={onFilter} />
          <span className="grow" />
          <button className="small" onClick={() => download(`${title.replace(/[^A-Za-z0-9_-]+/g, "-")}-submissions.csv`, submissionsCsv(subs))}>
            {t("exams:exportCsv")}
          </button>
        </div>
      )}
      {subs.length === 0 ? (
        <div className="empty-card" data-testid="no-submissions">
          <p className="muted small">{t("exams:noSubmissions")}</p>
          {staff.gradedHere ? (
            <>
              <p className="muted small">{t("exams:noSubmissionsHint")}</p>
              <CommandLine command="byj exam grade KIT --relay" />
            </>
          ) : (
            <p className="muted small">{t("console:noSubmissionsHint")}</p>
          )}
        </div>
      ) : (
        // a table, as the CSV is; on a phone it scrolls by itself, its cells kept whole
        <div className="doc-table exam-table" tabIndex={0} aria-label={t("exams:submissionsTitle")}>
          <table data-testid="exam-submissions">
            <thead>
              <tr>
                {person && <th>{staff.person()}</th>}
                <th>{t("exams:colQuestion")}</th>
                <th>{t("exams:colAgent")}</th>
                <th>{t("exams:colHandedIn")}</th>
                <th>{t("exams:colScore")}</th>
                <th>{t("exams:colState")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  {person && (
                    <td>
                      <button className="link" aria-controls="grade-drawer" onClick={() => setOpen(s.id)}>
                        {s.student?.name || s.student?.email}
                      </button>
                    </td>
                  )}
                  <td>
                    {/* one person's: the question opens the grade */}
                    {person ? (
                      staff.questionIds ? <code>{s.question}</code> : <span title={s.question}>{questionName(s.question)}</span>
                    ) : (
                      <button className="link" aria-controls="grade-drawer" title={s.question} onClick={() => setOpen(s.id)}>
                        {staff.questionIds ? s.question : questionName(s.question)}
                      </button>
                    )}
                  </td>
                  <td title={s.agent}>{s.agent ? agentLabel(s.agent) : "–"}</td>
                  <td>
                    #{s.n} {whenMinute(s.submitted_at)} {s.late && <span className="chip chip-warn">{t("classes:late")}</span>}
                  </td>
                  <td>{s.grade ? <Score g={s.grade} /> : "–"}</td>
                  <td>
                    <GradeChip sub={s} />{" "}
                    {openDispute(s) && <span className="chip chip-warn">{t("exams:disputeOpen")}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {shown && (
        <GradeDrawer
          sub={shown}
          questions={questions}
          role={role}
          review={review}
          onReviewed={(r) => {
            setSubs((xs) => (xs ?? []).map((x) => (x.id === shown.id ? { ...x, reviews: [...(x.reviews ?? []), r] } : x)));
            onChanged?.();
          }}
          patch={patch}
          answer={answer}
          onAnswered={(d) => {
            setSubs((xs) => (xs ?? []).map((x) => (x.id === shown.id ? { ...x, disputes: (x.disputes ?? []).map((o) => (o.id === d.id ? d : o)) } : x)));
            onChanged?.();
          }}
          onClose={() => setOpen(null)}
          onSaved={(g) => {
            setSubs((xs) => (xs ?? []).map((x) => (x.id === shown.id ? { ...x, grade: { ...g, detail: g.detail ?? x.grade?.detail } } : x)));
            onChanged?.();
          }}
        />
      )}
    </>
  );
}

/**
 * The bar under an assignment's head (byj-1wv.51.12): its window as its
 * state — open, not open yet, closed — then its class, a link to it (its
 * manage page's Exams tab for its instructor, its page for a student), and
 * when it opens and closes; then what the page adds.
 */
export function AssignmentBar({ page, now, children }: { page: ExamAssignmentPage; now: number; children?: React.ReactNode }) {
  const staff = useStaff();
  const a = page.assignment;
  const instructor = page.role === "instructor";
  const classPath = `/c/${encodeURIComponent(a.class.id)}${instructor ? "/manage" : ""}`;
  const state = windowState(a, now);
  return (
    <StateBar
      className="exam-bar"
      label={t("exams:examTabs")}
      state={state === "open" ? "live" : state === "closed" ? "ended" : "paused"}
      stateLabel={<span className="share-bar-state">{word("exams:examWindowState", state)}</span>}
      testid="exam-head-sub"
    >
      <span className="share-bar-facts">
        {/* Link's own click, and for the instructor the manage page's tab set to Exams before it opens (the tab is kept per browser, not in the address) */}
        {staff.classLink ? (
          <a
            href={classPath}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              if (instructor) openClassExams(a.class.id);
              else navigate(classPath);
            }}
          >
            {t("exams:examClass", { name: a.class.name })}
          </a>
        ) : (
          t("exams:examClass", { name: a.class.name })
        )}
        {" · "}
        {t("exams:examVersion", { v: a.version })}
        {a.opens_at && <> · {t("exams:opensAt", { w: whenMinute(a.opens_at) })}</>}
        {a.closes_at && <> · {t("exams:closesAt", { w: whenMinute(a.closes_at) })}</>}
      </span>
      {children}
    </StateBar>
  );
}

/**
 * A Share machine for this exam (byj-1wv.62.10): started here, on a fresh
 * home of its own, shared live with the class; opened once it has shared
 * itself. Nothing on a relay that hosts no machines, or for a student not
 * granted them, who sits it on their own machine. Outside the exam's window
 * the relay starts none (`canStart` false): nothing, unless the exam's own
 * machine still runs, opened to hand in what was begun there (byj-1wv.51.12).
 */
export function ExamMachine({ assignment, load, start, canStart = true }: {
  assignment: string;
  load: typeof api.machines;
  start: typeof api.startMachine;
  canStart?: boolean;
}) {
  const [data, setData] = useState<MachinesView | null>(null);
  const [off, setOff] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetch = useCallback(async () => {
    try {
      setData(await load());
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setOff(true);
    }
  }, [load]);
  useEffect(() => {
    void fetch();
  }, [fetch]);
  const running = data?.machines.filter((m) => m.state !== "stopped") ?? [];
  const mine = running.find((m) => m.exam === assignment);
  const waiting = mine !== undefined && !mine.share_id;
  useEffect(() => {
    if (off || !mine) return;
    const timer = setInterval(() => void fetch(), waiting ? pollStartingMs : pollMs);
    return () => clearInterval(timer);
  }, [fetch, off, mine, waiting]);
  if (off || !data || data.allowed === false) return null;
  if (!canStart && !mine) return null;
  const startIt = async () => {
    setBusy(true);
    try {
      await start(assignment);
      setError(null);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
      await fetch();
    }
  };
  // one line under the folds: what it is, what it does, and its button at the line's end (byj-1wv.51.12)
  return (
    <section aria-labelledby="exam-machine" className="card exam-machine" data-testid="exam-machine">
      <div className="exam-machine-words">
        <h2 id="exam-machine" className="flush">{t("machines:examMachineTitle")}</h2>
        <p className="muted small flush">{t("machines:examMachineLead")}</p>
        {error && <p className="error flush">{t("common:error", { m: error })}</p>}
      </div>
      {mine ? (
        <div className="row wrap exam-machine-act">
          <span className={waiting ? "chip" : "chip chip-ok"}>{waiting ? t("machines:machineStarting") : t("machines:machineRunning")}</span>
          {/* its exam room: the machine's workbench, sat for this exam (RFC-058) */}
          <button className="primary" disabled={waiting} onClick={() => mine.share_id && navigate(`/s/${encodeURIComponent(mine.share_id)}/workbench`)}>
            {t("machines:examMachineOpen")}
          </button>
          <Link to="/machines" className="button small">{t("machines:examMachineManage")}</Link>
        </div>
      ) : running.length > 0 ? (
        <p className="small flush exam-machine-act">
          {t("machines:examMachineOther")} <Link to="/machines">{t("machines:examMachineManage")}</Link>
        </p>
      ) : (
        <div className="row exam-machine-act">
          <button className="primary" disabled={busy || data.month_seconds >= data.month_cap_seconds} onClick={() => void startIt()}>
            {t("machines:examMachineStart")}
          </button>
        </div>
      )}
    </section>
  );
}

